"use client"

import * as React from "react"
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip"

import { cn } from "@/lib/utils"

function TooltipProvider({
  delay = 0,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delay}
      {...props}
    />
  )
}

// Base UI tooltips open on hover/focus only. On a touch device a tap focuses
// the trigger (which opens it) but the tap's own pointerup immediately closes
// it again — so it flashes and looks like "nothing happened". We make the Root
// controllable, force it open on tap, and swallow Base UI's immediate close for
// a short lock window. Only touch devices (hover: none) get this — mouse hover
// is untouched. Context hands the trigger the open state + toggle.
const isTouch = () =>
  typeof window !== "undefined" && window.matchMedia("(hover: none)").matches

interface TooltipTapControls {
  isOpen: boolean
  openByTap: () => void
  close: () => void
}
const TooltipTapContext = React.createContext<TooltipTapControls | null>(null)

function Tooltip({
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: TooltipPrimitive.Root.Props) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false,
  )
  const isControlled = open !== undefined
  const currentOpen = isControlled ? open : uncontrolledOpen
  // While set (a tap just opened us), ignore Base UI's spurious close.
  const lockUntilRef = React.useRef(0)

  const handleOpenChange = React.useCallback<
    NonNullable<TooltipPrimitive.Root.Props["onOpenChange"]>
  >(
    (nextOpen, ...rest) => {
      if (!nextOpen && Date.now() < lockUntilRef.current) return
      if (!isControlled) setUncontrolledOpen(nextOpen)
      onOpenChange?.(nextOpen, ...rest)
    },
    [isControlled, onOpenChange],
  )

  const tap = React.useMemo<TooltipTapControls>(
    () => ({
      isOpen: currentOpen,
      openByTap: () => {
        lockUntilRef.current = Date.now() + 600
        if (!isControlled) setUncontrolledOpen(true)
      },
      close: () => {
        lockUntilRef.current = 0
        if (!isControlled) setUncontrolledOpen(false)
      },
    }),
    [currentOpen, isControlled],
  )

  return (
    <TooltipTapContext.Provider value={tap}>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        open={currentOpen}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </TooltipTapContext.Provider>
  )
}

function TooltipTrigger({
  onClick,
  ...props
}: TooltipPrimitive.Trigger.Props) {
  const tap = React.useContext(TooltipTapContext)
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      onClick={(event) => {
        onClick?.(event)
        if (tap && isTouch()) {
          if (tap.isOpen) tap.close()
          else tap.openByTap()
        }
      }}
      {...props}
    />
  )
}

function TooltipContent({
  className,
  side = "top",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  children,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<
    TooltipPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset"
  >) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        // Fixed positioning keeps the portaled popup out of the document's
        // scroll size so it can't grow the page / zoom mobile out (see select.tsx).
        positionMethod="fixed"
        className="isolate z-50"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            "z-50 inline-flex w-fit max-w-xs origin-(--transform-origin) items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground has-data-[slot=kbd]:pr-1.5 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-sm data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            className
          )}
          {...props}
        >
          {children}
          <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] bg-primary fill-primary data-[side=bottom]:top-1 data-[side=inline-end]:top-1/2! data-[side=inline-end]:-left-1 data-[side=inline-end]:-translate-y-1/2 data-[side=inline-start]:top-1/2! data-[side=inline-start]:-right-1 data-[side=inline-start]:-translate-y-1/2 data-[side=left]:top-1/2! data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2 data-[side=right]:top-1/2! data-[side=right]:-left-1 data-[side=right]:-translate-y-1/2 data-[side=top]:-bottom-2.5" />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
